import type { Serie } from '@nivo/line';
import { ResponsiveLine } from '@nivo/line';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useMemo } from 'react';

import type { Granularity } from '../../models';
import { formatPeriodLong, formatPeriodTick } from './periodFormat';

interface DashboardLineChartProps {
  /** Nivo series whose `x` values are period keys from the Statistics module. */
  data: Serie[];
  granularity: Granularity;
  height?: number;
}

const DEFAULT_HEIGHT = 360;
/** Roughly how many ticks fit on the axis before the labels start colliding. */
const MAX_TICKS = 8;

/**
 * The dashboard's time-series chart. Both the overview and the dispatches tab plot period-keyed
 * counters, so the axis formatting and the tooltip live here rather than being duplicated — and the
 * period keys (`2025-W47`) get turned into something readable on the way out.
 */
const DashboardLineChart: React.FC<DashboardLineChartProps> = ({
  data,
  granularity,
  height = DEFAULT_HEIGHT
}) => {
  const { tDashboard } = useCustomTranslation();

  const tickValues = useMemo(() => {
    const periods = data[0]?.data.map((d) => d.x as string) ?? [];
    const step = Math.max(1, Math.ceil(periods.length / MAX_TICKS));
    return periods.filter((_, i) => i % step === 0);
  }, [data]);

  return (
    <div style={{ height }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 20, right: 130, bottom: 70, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto' }}
        curve="monotoneX"
        enablePoints={false}
        useMesh
        axisBottom={{
          tickRotation: -45,
          tickValues,
          format: (value) => formatPeriodTick(String(value), granularity, tDashboard)
        }}
        tooltip={({ point }) => (
          <div
            style={{
              background: 'white',
              padding: '6px 10px',
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 12
            }}
          >
            <div style={{ marginBottom: 4 }}>
              {formatPeriodLong(String(point.data.x), granularity, tDashboard)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: point.serieColor,
                  display: 'inline-block'
                }}
              />
              <strong>{point.serieId}:</strong> {point.data.yFormatted}
            </div>
          </div>
        )}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            translateX: 120,
            itemWidth: 110,
            itemHeight: 20,
            symbolSize: 12,
            symbolShape: 'circle'
          }
        ]}
      />
    </div>
  );
};

export default DashboardLineChart;
